/**
 * Assignment provenance for party-class reference slots (SPEC-SOR-220-v2).
 *
 * A slot — one document × one party-class code — carries a recorded fact about
 * HOW its assignment came to be, so a guard can answer "is this slot still
 * trustworthy" from the draft itself rather than from render order: the roster
 * swap and the effect that reacts to it race, but the recorded fact does not.
 *
 * ONE DECIDER, ONE READER. `nextAssignmentProvenance` is the sole authority on
 * what a write makes a slot's provenance; `getAssignmentProvenance` is the sole
 * reader that resolves absence. A writer that stamps the map by hand, or a
 * reader that peeks at `doc.assignmentProvenance` directly, bypasses both and
 * is how the three writers (filer act, gap-fill, case change) grow their own
 * opinions of what a slot is.
 *
 * Widening the code set needs NO schema change (SPEC-SOR-258-v1): the persisted
 * `assignmentProvenance` map in `filing.schema.ts` is keyed
 * `z.record(z.string(), z.enum(ASSIGNMENT_PROVENANCE_VALUES))` — by string, not
 * by this union — so a fifth code persists through the existing schema.
 *
 * Like `guided-sections.ts` this is a deliberate leaf: zero imports, narrow
 * structural slices instead of `EFilingWizardState`, and the `.js` extensions
 * below are the Docker-build ESM rule, not an optional style.
 */
/** The canonical spelling of the five codes; nothing else may enumerate them. */
export const PARTY_CLASS_CODES = [
    'FILING_PARTY',
    'FILING_ATTORNEY',
    'PARTY_SERVED',
    'REFERS_TO_PARTIES',
    'FORMER_ATTORNEY',
];
/** The one spelling of the four values the persisted schema's enum consumes. */
export const ASSIGNMENT_PROVENANCE_VALUES = [
    'filer_chosen',
    'prefilled',
    'cleared',
    'untouched',
];
/**
 * The provenance a write leaves on a slot — the ONE decider.
 *
 * `write` alone decides the outcome: the filer's own act re-arms a slot to
 * `filer_chosen` (a cleared slot resolves only by being assigned again), a
 * gap-fill is a prefill and never a choice, and a case-change clear holds the
 * slot at `cleared`. `current` names the from-state at the call site and is
 * deliberately UNREAD — no write may launder a slot by choosing its starting
 * point. The function never returns `untouched`: that value is only ever read
 * out of absence by `getAssignmentProvenance`.
 */
export function nextAssignmentProvenance({ write, }) {
    switch (write) {
        case 'filer':
            return 'filer_chosen';
        case 'gapfill':
            return 'prefilled';
        case 'case_change_clear':
            return 'cleared';
    }
}
/**
 * A slot's provenance, with absence resolved — the ONE reader.
 *
 * The map is optional and sparse by design (the `sectionStatuses` discipline):
 * a draft saved before provenance existed carries no map at all, and spelling
 * the key on a default object would clobber every persisted value on the
 * portal's `{ ...defaultWizardState, ...typeData }` spread. So:
 *
 * - a tagged entry wins;
 * - an untagged slot HOLDING a reference is defensively read `filer_chosen` —
 *   a loaded legacy draft's reference is exactly what the filer's own
 *   document-level pick produced;
 * - anything else is `untouched`.
 *
 * "Holding a reference" is the repo's one reference shape — a NON-EMPTY array
 * of strings under `metadataValues[code]`, the identical guard
 * `referencesResolve` and `collectExtractionSourcedReferences` apply. An empty
 * array, a scalar codeList, and a new-data object array are not references.
 */
export function getAssignmentProvenance(doc, code) {
    const tagged = doc.assignmentProvenance?.[code];
    if (tagged !== undefined)
        return tagged;
    const value = doc.metadataValues?.[code];
    if (Array.isArray(value) && value.length > 0 && value.every(v => typeof v === 'string')) {
        return 'filer_chosen';
    }
    return 'untouched';
}
